/**
 * Shared OAuth-grant timestamp formatters.
 *
 * The workspace-admin audit list and the user's personal connected-apps
 * screen render the same OAuthConsent rows from different endpoints —
 * keep them visually consistent by sourcing the formatters from one
 * place so they can't drift.
 */

export function relativeDate(iso: string): string {
  // Lightweight relative formatter — exact second precision isn't
  // useful here; days/hours is plenty. Keeps a dependency off the
  // bundle.
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function absoluteDate(iso: string): string {
  // Compact absolute datetime so multiple grants from the same AI
  // client are distinguishable even when the relative form collapses
  // them to "23d ago". Local timezone, locale-aware via Intl.
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  return then.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
